import { useEffect, useRef } from 'react';
import type React from 'react';

import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Text } from '@change/ds-core-ui';
import { ArrowLeftIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { useI18n } from '@change-corgi/core-react/i18n';

import { useKeepModalVisibleAboveKeyboard, useScrollContainerOverflow } from '#src/app/shared/hooks/dom';
import { focus } from '#src/app/shared/utils/dom';

function noop(): void {
	/* see the `onClose` comment on the Modal below */
}

// Same list as the rich text editor's `attachExitOnEscape`.
const TABBABLE_SELECTOR = [
	'a[href]',
	'button:not([disabled])',
	'input:not([disabled])',
	'select:not([disabled])',
	'textarea:not([disabled])',
	'[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * The body is the sheet's only scroller, and on the Sent stage nothing inside it can take focus, so
 * once it overflows it needs its own tab stop or keyboard users cannot scroll it (CHANGE-100000).
 * Only while it overflows and has nothing tabbable inside: a focused child already scrolls the body,
 * and a focusable body would steal a tap on its padding from a textarea mid-edit. The attributes
 * are set through the ref because the DS `ModalBody` types do not declare `tabIndex`/`role`, though
 * the runtime spreads them onto the div. `label` names the region; the heading already names the
 * dialog, so it does the same job here.
 */
function useBodyTabStop(
	bodyRef: React.RefObject<HTMLDivElement | null>,
	anchorRef: React.RefObject<HTMLDivElement | null>,
	isOpen: boolean,
	label: string,
): void {
	// The DS mounts the body only while open, so re-measure when the sheet opens.
	const isBodyOverflowing = useScrollContainerOverflow(anchorRef, String(isOpen));
	// A closed sheet has no body to measure, so `isBodyOverflowing` keeps its last value; gating on
	// `isOpen` makes a reopen re-apply the attributes to the newly mounted body.
	const isOverflowing = isOpen && isBodyOverflowing;
	useEffect(() => {
		const body = bodyRef.current;
		if (!body) return;
		if (isOverflowing && !body.querySelector(TABBABLE_SELECTOR)) {
			body.tabIndex = 0;
			body.setAttribute('role', 'region');
			body.setAttribute('aria-label', label);
		} else if (body.ownerDocument.activeElement !== body) {
			// ponytail: a focused body keeps its stop until the next overflow change, so focus never
			// drops out of the dialog; remove it on blur if a stale stop ever matters.
			body.removeAttribute('tabindex');
			body.removeAttribute('role');
			body.removeAttribute('aria-label');
		}
	}, [bodyRef, isOverflowing, label]);
}

// Styling the back button has to go through `leadingSlotClassName`: ModalHeader clones the slot with
// `{ size: 'medium', className: leadingSlotClassName }`, so a `className` on the Button itself is
// dropped. That forced `size="medium"` also matches none of Button's real sizes (large | small |
// x-small), which is why the focus ring is spelled out here rather than inherited from the variant.
const BACK_BUTTON_CLASSNAME = cn(
	// A bare arrow, like the Starter Dashboard's "Petition activity" header.
	'p-0 no-underline',
	'focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-solid',
	'focus-visible:outline-stroke-focus',
);

// The DS caps the header's title slot at 282px (351px from `md`) to clear the close button, which
// wrapped every heading a line early. There is no prop for it, so the cap is lifted through the
// `slot="title"` wrapper the DS renders, and `pr-10` takes over the job of clearing the X. Same
// reach-through as the footer's column override — if the DS renames the slot, both need revisiting.
const TITLE_SLOT_CLASSNAME = '[&_[slot=title]]:max-w-none [&_[slot=title]]:pr-10';

// The design draws no line between the body and the CTAs. ModalFooter adds one by itself whenever
// the body scrolls, on its OUTER wrapper — the one its `className` never reaches — with no prop to
// opt out. Goes on the body: that wrapper is its next sibling inside the dialog (CHANGE-99560).
const NO_FOOTER_SEPARATOR_CLASSNAME = '[&+div]:border-t-0';

/**
 * The body's own height cap keeps a default sheet to ~85dvh. A tall sheet only needs it from `md`,
 * where the DS dialog is content-sized again — unless the stage asked for the dialog to fit its
 * content there too, in which case the panel cap alone decides (CHANGE-99691).
 */
function bodyMaxHeightClassName(fullHeightOnMobile: boolean, fitContentOnDesktop: boolean): string | undefined {
	if (!fullHeightOnMobile) return 'max-h-[calc(85dvh-80px)]';
	return fitContentOnDesktop ? undefined : 'md:max-h-[calc(85dvh-80px)]';
}

type ComposeSheetAction = {
	label: string;
	onClick: () => void;
	disabled?: boolean;
	loading?: boolean;
	/** Icon before the label — the sparkle that marks the draft CTA as AI-assisted. */
	leadingIcon?: React.ReactNode;
};

type Props = {
	isOpen: boolean;
	/** Single dismissal path: the close button, ESC and outside-click all arrive here. */
	onOpenChange: (isOpen: boolean) => void;
	/**
	 * Below `md`, stretch the bottom sheet to the DS's tallest sheet height (a 10px strip of backdrop
	 * stays visible above it) whatever the stage holds, instead of the content-sized default capped at
	 * ~85dvh; above it, the usual centred dialog. The story, review and sent stages opt in so the sheet
	 * keeps one height from stage to stage. The reason picker stays a default sheet.
	 */
	fullHeightOnMobile?: boolean;
	/**
	 * From `md`, let the dialog grow to its content up to 100dvh minus 4rem (the DS's own margin) instead of its 752px
	 * panel cap, and drop the body's own cap, so a stage whose content is a little taller than the DS
	 * allows (the Sent screen's promise card) reads without scrolling (CHANGE-99691).
	 */
	fitContentOnDesktop?: boolean;
	/** Optional uppercase kicker above the heading — "Review message". The story screens have none. */
	eyebrow?: string;
	/** Names the dialog, and is the header's title unless `hideHeading` moves it into the body. */
	heading: string;
	/**
	 * Leave the header to the back arrow and the X. The sent stage draws its heading in the body, under
	 * the megaphone; `heading` still names the dialog. Hides the eyebrow with it.
	 */
	hideHeading?: boolean;
	/**
	 * Omit on the first stage, which has nowhere to go back to. The label is not optional: the button
	 * is icon-only, so it has no accessible name without one.
	 */
	back?: { onClick: () => void; ariaLabel: string };
	/** Full-width footer CTA. Omit for a stage with no primary action (the drafting spinner). */
	primaryAction?: ComposeSheetAction;
	/**
	 * Underlined link above the primary CTA — "Continue with my own words". Takes `disabled` because
	 * it can carry the supporter forward with a payload of its own, so it needs the same guard the
	 * primary does; it never shows a spinner.
	 */
	tertiaryAction?: Omit<ComposeSheetAction, 'loading' | 'leadingIcon'>;
	children: React.ReactNode;
};

/**
 * Bottom-sheet chrome shared by the compose stages (component spec, "Shared: bottom-sheet chrome").
 *
 * Each stage renders its own `ComposeSheet` with its own eyebrow, heading and footer, rather than one
 * sheet swapping its insides. That mirrors `PetitionShareSlideUp`.
 *
 * Because of that, a sheet unmounts on every stage change while still `isOpen`, so this component
 * cannot own focus restoration — `ContactDecisionMakerFlow` does it once for the whole flow.
 */
export const ComposeSheet: React.FC<Props> = ({
	isOpen,
	onOpenChange,
	fullHeightOnMobile = false,
	fitContentOnDesktop = false,
	eyebrow,
	heading,
	hideHeading = false,
	back,
	primaryAction,
	tertiaryAction,
	children,
}) => {
	const { translate } = useI18n();
	const anchorRef = useKeepModalVisibleAboveKeyboard();

	const bodyRef = useRef<HTMLDivElement>(null);
	useBodyTabStop(bodyRef, anchorRef, isOpen, heading);

	return (
		<Modal
			isOpen={isOpen}
			// `onClose` only exists to make the DS render the X; it must stay a no-op so dismissal does
			// not double-fire. See PetitionShareSlideUp.
			onClose={noop}
			closeButtonAriaLabel={translate('corgi.components.modal.close_button')}
			onOpenChange={onOpenChange}
			aria-label={heading}
			// The tall stages stretch the sheet PANEL to `h-full`, which its own `max-h` (100dvh minus 10px)
			// caps — the same mechanism as the deprecated `fullScreenOnMobile`, minus its squared corners
			// and removed backdrop strip. `ModalBody` (`flex-1 min-h-0` in the DS) grows to fill it. The DS
			// puts this `className` on its overlay and its layout wrapper, never on the panel, so the
			// height goes through `[&>div]`: from the wrapper that is the panel; from the overlay it is the
			// wrapper. A plain height here shrinks the overlay and wrapper by 10px instead, leaving a gap
			// under the sheet (CHANGE-99560). The desktop cap goes the same way: the DS sets its 752px
			// `max-h` with `!`, so lifting it needs `!` at higher specificity (CHANGE-99691).
			//
			// `max-md:h-full` is for every sheet: the DS wrapper is `h-dvh` inside an `inset-0` overlay,
			// and where the browser's dvh is shorter than the viewport a content-sized sheet stops short
			// of the bottom. Following the overlay's height keeps the panel flush; on the overlay itself
			// `h-full` is the viewport, which `inset-0` already is.
			className={cn(
				'z-100 max-md:h-full',
				TITLE_SLOT_CLASSNAME,
				fullHeightOnMobile && 'max-md:[&>div]:h-full',
				fitContentOnDesktop && 'md:[&>div]:max-h-[calc(100dvh-4rem)]!',
			)}
		>
			{/* Must stay a DIRECT child of `Modal` — see PetitionShareSlideUp. */}
			<ModalHeader
				title={
					hideHeading ? undefined : (
						<div className="flex flex-col gap-1">
							{eyebrow && (
								// Spec asks for `--color-typography-weaker`, which Sunrise doesn't ship; `weak` is the
								// weakest text token that exists.
								<Text as="span" className="text-size-body-xs-bold text-typography-weak tracking-[0.06em] uppercase">
									{eyebrow}
								</Text>
							)}
							{/* Spec calls for 21px/800, which sits between two steps of the type scale; rounded down to
						    heading-xs (18px/700) to stay on-token, matching the sibling share sheet's 19px → xs. */}
							<Text as="span" className="text-size-heading-xs text-typography-strong">
								{heading}
							</Text>
						</div>
					)
				}
				leadingSlotClassName={BACK_BUTTON_CLASSNAME}
				leadingSlot={
					back ? (
						// iconOnly buttons render no children — the accessible name is the aria-label.
						<Button
							iconOnly
							variant="tertiary"
							leadingIcon={<ArrowLeftIcon size="medium" />}
							aria-label={back.ariaLabel}
							onClick={back.onClick}
						/>
					) : undefined
				}
			/>

			{/* `-mx-1 px-1`: the body is the scroll container and clips at its own edge, which cut the sides
			    off the textarea's focus ring; growing it 4px into the sheet padding keeps the ring inside.
			    `pb-1` does the same for the bottom edge, which the footer sits right under. */}
			<ModalBody
				ref={bodyRef}
				className={cn(
					'-mx-1 px-1 pb-1',
					'focus-visible:outline-stroke-focus focus-visible:outline-2 focus-visible:outline-offset-2',
					NO_FOOTER_SEPARATOR_CLASSNAME,
					bodyMaxHeightClassName(fullHeightOnMobile, fitContentOnDesktop),
				)}
				data-testid="contact-decision-maker-compose-sheet-body"
			>
				<div ref={anchorRef} className="w-full">
					{children}
				</div>
			</ModalBody>

			{(primaryAction || tertiaryAction) && (
				<ComposeSheetFooter primaryAction={primaryAction} tertiaryAction={tertiaryAction} />
			)}
		</Modal>
	);
};

type FooterProps = Pick<Props, 'primaryAction' | 'tertiaryAction'>;

/** The footer chrome: primary CTA and the text link above it. */
const ComposeSheetFooter: React.FC<FooterProps> = ({ primaryAction, tertiaryAction }) => {
	const primaryRef = useRef<HTMLButtonElement>(null);
	const isPrimaryDisabled = Boolean(primaryAction?.disabled || primaryAction?.loading);
	const wasPrimaryDisabledRef = useRef(isPrimaryDisabled);

	// A disabled control cannot hold focus: the browser moves it to `<body>`, outside the dialog, where
	// react-aria's Escape handler (bound to the overlay, not the document) cannot hear it, and only the
	// next Tab finds the way back in. The DS Button is disabled while `loading` too, so the primary
	// loses focus the moment it is pressed. When it becomes disabled, focus goes to the dialog — where
	// react-aria puts it on open — unless it rests on an enabled control in the sheet; when it is
	// enabled again, focus parked on the dialog returns to it, so a retry is one keypress. Only the
	// transitions count: on mount, react-aria's own dialog focus is left to do its job.
	useEffect(() => {
		const wasPrimaryDisabled = wasPrimaryDisabledRef.current;
		wasPrimaryDisabledRef.current = isPrimaryDisabled;
		if (isPrimaryDisabled === wasPrimaryDisabled) return;

		const primary = primaryRef.current;
		const dialog = primary?.closest<HTMLElement>('[role="dialog"]');
		if (!primary || !dialog) return;
		const active = document.activeElement;

		if (!isPrimaryDisabled) {
			if (active === dialog) focus(primary);
			return;
		}
		if (active && dialog.contains(active) && !active.matches(':disabled')) return;
		focus(dialog, { makeFocusable: true });
	}, [isPrimaryDisabled]);

	return (
		<>
			{/* The text link renders ABOVE the primary at every width, as the design draws every two-CTA
		    screen ("MVP - All Supporters.dc.html", `storyFooter`). It is done with `order-first` on the link
		    rather than by reordering the children: the DS asserts the primary Button is ModalFooter's
		    first child and owns the buttons' flex container (`flex-col`, `md:flex-row-reverse`), so a
		    `className` here lands on the wrapper, not on it. The arbitrary-variant classes below reach
		    through to that container and keep it a stretched column on desktop too — the design review
		    asked for one orientation on both (CHANGE-98939, CHANGE-99399). */}
			<ModalFooter
				className="md:[&>div>div:first-child]:flex-col! md:[&>div>div:first-child]:items-stretch!"
				data-testid="contact-decision-maker-compose-sheet-footer"
			>
				{/* A static (non-function) ModalFooter body has DS v13 call `close()` after every Button's
				    own onClick — meant for simple confirm/cancel footers. Our actions drive the flow's state
				    machine, not the modal's open state, so the render-prop form opts back out of that and
				    leaves closing to `onOpenChange` (ESC, outside click, the header's X). */}
				{() => (
					<>
						{primaryAction && (
							<Button
								ref={primaryRef}
								variant="primary"
								// The DS clones footer buttons with `md:w-auto`; ours stay full width like the mobile design.
								className="w-full md:w-full"
								disabled={primaryAction.disabled}
								loading={primaryAction.loading}
								leadingIcon={primaryAction.leadingIcon}
								onClick={primaryAction.onClick}
							>
								{primaryAction.label}
							</Button>
						)}
						{tertiaryAction && (
							<Button
								variant="tertiary"
								size="small"
								className="order-first mx-auto underline"
								onClick={tertiaryAction.onClick}
								disabled={tertiaryAction.disabled}
							>
								{tertiaryAction.label}
							</Button>
						)}
					</>
				)}
			</ModalFooter>
		</>
	);
};
